import { useEffect } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShieldCheck } from 'lucide-react'

import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { readSessionAfterLogin, useLogin } from '@/features/auth/api'
import { ApiError } from '@/lib/api'

const schema = z.object({
  email: z.string().min(1, 'Email wajib diisi.').email('Format email belum benar.'),
  password: z.string().min(1, 'Kata sandi wajib diisi.'),
})

type FormValues = z.infer<typeof schema>

const route = getRouteApi('/masuk')

export function LoginPage() {
  const search = route.useSearch()
  const navigate = useNavigate()
  const login = useLogin()
  const toast = useToast()
  const queryClient = useQueryClient()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  // Dasbor adalah tujuan hampir setiap orang sesudah masuk, dan potongan
  // kodenya termasuk yang terbesar. Mengunduhnya sekarang — sementara
  // pengguna masih mengetik — membuat perpindahan sesudah masuk tidak lagi
  // menunggu unduhan. Kegagalannya diabaikan: ini percepatan, bukan syarat.
  useEffect(() => {
    void import('@/routes/dashboard').catch(() => {})
  }, [])

  const submit = form.handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: async () => {
        // Cookie sesi bersifat HttpOnly dan tidak dapat dibaca JavaScript,
        // sehingga satu-satunya cara memastikan peramban benar-benar
        // menyimpannya adalah bertanya balik ke server.
        //
        // Tanpa pemeriksaan ini, peramban yang menolak cookie akan
        // memantulkan pengguna kembali ke halaman ini tanpa satu pun pesan:
        // dari sisi mereka, menekan "Masuk" seolah tidak melakukan apa-apa.
        const session = await readSessionAfterLogin(queryClient)
        if (!session) {
          toast.error(
            'Sesi tidak tersimpan',
            'Masuk Anda berhasil, tetapi peramban ini menolak menyimpan cookie sesi. ' +
              'Biasanya karena membuka lewat aplikasi lain (WhatsApp, Instagram), mode penyamaran, ' +
              'atau pemblokir iklan. Coba buka skava.id langsung di Chrome atau Safari.',
          )
          return
        }

        // Tujuan setelah masuk mengikuti halaman yang tadi hendak dibuka,
        // supaya pengguna kembali ke tempat ia terputus.
        await navigate({ to: search.redirect ?? '/app' })
      },
      onError: (error) => {
        if (error instanceof ApiError) {
          toast.error('Gagal masuk', error.message)
          return
        }
        toast.error('Gagal masuk', 'Periksa koneksi Anda lalu coba lagi.')
      },
    })
  })

  return (
    <AuthLayout
      title="Masuk ke Skava"
      subtitle="Lanjutkan belajar, kelola kelas, atau pantau proyek Anda."
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="nama@perusahaan.id"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Input
          label="Kata sandi"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        <div className="flex justify-end">
          <Link to="/lupa-sandi" className="text-sm font-semibold text-brand hover:underline">
            Lupa kata sandi?
          </Link>
        </div>

        <Button type="submit" fullWidth size="lg" loading={login.isPending}>
          Masuk
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted">
        Belum punya akun?{' '}
        <Link to="/daftar" className="font-semibold text-brand hover:underline">
          Daftar gratis
        </Link>
      </p>

      {import.meta.env.DEV ? <DemoAccounts onPick={(email) => form.setValue('email', email)} /> : null}
    </AuthLayout>
  )
}

/**
 * Daftar akun contoh hanya dirender saat pengembangan.
 *
 * Pemeriksaan import.meta.env.DEV dievaluasi saat build, sehingga blok ini
 * tidak ikut terbawa ke berkas produksi sama sekali.
 */
function DemoAccounts({ onPick }: { onPick: (email: string) => void }) {
  const accounts = [
    { email: 'sinta@skava.id', role: 'Peserta' },
    { email: 'budi@skava.id', role: 'Tenaga Ahli' },
    { email: 'rendi@skava.id', role: 'Klien' },
    { email: 'finance@skava.id', role: 'Finance' },
    { email: 'mediator@skava.id', role: 'Mediator' },
  ]

  return (
    <div className="mt-8 rounded-xl border border-dashed border-line p-4">
      <p className="text-xs font-bold uppercase tracking-wide text-subtle">Akun contoh (mode pengembangan)</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {accounts.map((account) => (
          <button
            key={account.email}
            type="button"
            onClick={() => onPick(account.email)}
            className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-muted transition hover:border-brand hover:text-brand"
          >
            {account.role}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-subtle">Kata sandi seluruh akun contoh: SkavaDemo123!</p>
    </div>
  )
}

export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    // dvh, bukan vh: pada peramban ponsel, vh menghitung tinggi layar
    // termasuk bilah alamat yang menghilang saat digulir, sehingga formulir
    // terdorong keluar layar tepat ketika pengguna mulai mengetik.
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-6 sm:px-8">
        <div className="w-full max-w-lg">
          <Link to="/" className="inline-block">
            <Logo />
          </Link>

          <h1 className="mt-5 text-2xl font-bold tracking-[-0.03em] text-ink sm:text-3xl">{title}</h1>
          <p className="mt-1.5 text-muted">{subtitle}</p>

          <div className="mt-5">{children}</div>
        </div>
      </div>

      {/* Panel kanan hanya hiasan bermakna: menegaskan janji produk pada
          momen pengguna memutuskan membuat akun. */}
      <aside className="relative hidden overflow-hidden border-l border-line bg-page lg:block">
        <div aria-hidden className="grid-lines pointer-events-none absolute inset-0" />

        <div className="relative flex h-full flex-col justify-between p-12 text-ink">
          <span className="mono-label inline-flex w-fit items-center gap-2 rounded-full bg-inverted px-3.5 py-2 text-[13px] text-on-inverted">
            <ShieldCheck className="size-4" />
            Dana dikunci escrow
          </span>

          <p className="text-3xl font-bold leading-[1.15] tracking-[-0.03em]">
            Berapa yang dipotong, kapan dana cair, dan apa yang terjadi kalau ada sengketa — semuanya
            tertulis sebelum Anda menyetujui apa pun.
          </p>

          <dl className="grid grid-cols-3 gap-6">
            {[
              { label: 'Pencatatan', value: 'Berpasangan' },
              { label: 'Pelepasan', value: 'Per milestone' },
              { label: 'Jejak audit', value: 'Tak terubah' },
            ].map((item) => (
              <div key={item.label} className="border-t border-line pt-3">
                <dt className="mono-label text-[11px] text-subtle">{item.label}</dt>
                <dd className="mt-1.5 text-lg font-bold">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>
    </div>
  )
}
